/**
 * Shopify GraphQL Service
 * 
 * This service handles all GraphQL communication with the Shopify API.
 * It provides a centralized way to make API calls, handle rate limiting,
 * and manage retries.
 */
import { getServerUrl } from '../config';

export class ShopifyGraphQLService {
  constructor(shop, accessToken) {
    // Clean shop URL to just the domain
    this.shop = shop.replace(/^https?:\/\//, '').replace(/\/$/, '');
    this.accessToken = accessToken;
  }

  /**
   * Makes a GraphQL request to the Shopify API via the app's proxy
   * @param {object} data - GraphQL request data
   * @returns {Promise<object>} - Response data
   */
  async makeGraphQLRequest(data) {
    try {
      if (!this.shop || !this.accessToken) {
        throw new Error('Shop and access token are required');
      }

      // Handle both string and object query formats
      const query = data.query;
      const variables = data.variables || {};

      // Get the proxy server URL from centralized configuration
      const proxyUrl = `${getServerUrl('proxy')}/api/shopify/graphql`;

      // Log request details for debugging
      console.log('Making GraphQL request (API 2025-10):', {
        url: proxyUrl,
        shop: this.shop,
        query: typeof query === 'string' ? query.slice(0, 100) + '...' : JSON.stringify(query),
        variables: JSON.stringify(variables)
      });

      const response = await fetch(proxyUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          query,
          variables,
          shop: this.shop,
          accessToken: this.accessToken
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = errorText;
        }
        
        console.error('API Error Response:', errorData);
        console.error('Request Body:', {
          query: typeof query === 'string' ? query.slice(0, 100) + '...' : JSON.stringify(query),
          variables: JSON.stringify(variables),
          shop: this.shop
        });
        
        throw new Error(`Shopify API error: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const result = await response.json();
      
      if (result.errors) {
        console.error('GraphQL Error Response:', JSON.stringify(result.errors, null, 2));
        throw new Error(`Shopify API error: ${result.errors[0].message}`);
      }

      // Log API cost for rate limiting
      if (result.extensions?.cost) {
        const cost = result.extensions.cost;
        console.log(`API Cost: ${cost.actualQueryCost} (throttle: ${cost.throttleStatus.currentlyAvailable}/${cost.throttleStatus.maximumAvailable})`);
      }

      return result.data;
    } catch (error) {
      console.error('Error making GraphQL request:', error);
      throw error;
    }
  }

  /**
   * Executes a GraphQL query with proper error handling and retries
   * @param {string|object} query - The GraphQL query string or object with query and variables
   * @param {object} variables - Variables for the query (if query is a string)
   * @returns {Promise<object>} Query result
   */
  async query(query, variables = {}) {
    if (typeof query === 'object' && query.query) {
      return this.makeGraphQLRequest(query);
    }
    return this.makeGraphQLRequest({ query, variables });
  }

  /**
   * Executes a GraphQL mutation with proper error handling and retries
   * @param {string|object} mutation - The GraphQL mutation string or object with query and variables
   * @param {object} variables - Variables for the mutation (if mutation is a string)
   * @returns {Promise<object>} Mutation result
   */
  async mutate(mutation, variables = {}) {
    if (typeof mutation === 'object' && mutation.query) {
      return this.makeGraphQLRequest(mutation);
    }
    return this.makeGraphQLRequest({ query: mutation, variables });
  }
}
